import '../styles/AdminConfigurationConsole.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  projectData,
  useProjectCollection,
} from '../lib/projectData';

const CONFIG_COLLECTION = 'service_configuration';

const WEIGHT_UNITS = ['kg', 'g', 'lb', 'oz'];
const DIMENSION_UNITS = ['cm', 'mm', 'in'];
const DEFAULT_STAGES = [
  'Picked up',
  'In transit',
  'Customs',
  'Out for delivery',
  'Delivered',
];
const SERVICE_NAME_MAX = 80;
const STAGE_COUNT_MIN = 2;
const STAGE_COUNT_MAX = 8;
const STAGE_LABEL_MAX = 32;

const EMPTY_DRAFT = {
  serviceName: '',
  stageVocabulary: [],
  weightUnit: WEIGHT_UNITS[0],
  dimensionUnit: DIMENSION_UNITS[0],
};

/* ---------------------------------------------------------------- model */

function configToDraft(record) {
  if (!record) return EMPTY_DRAFT;
  return {
    serviceName: typeof record.serviceName === 'string' ? record.serviceName : '',
    stageVocabulary: Array.isArray(record.stageVocabulary)
      ? record.stageVocabulary.slice()
      : [],
    weightUnit:
      typeof record.weightUnit === 'string' && record.weightUnit
        ? record.weightUnit
        : WEIGHT_UNITS[0],
    dimensionUnit:
      typeof record.dimensionUnit === 'string' && record.dimensionUnit
        ? record.dimensionUnit
        : DIMENSION_UNITS[0],
  };
}

function draftsMatch(a, b) {
  if (!a || !b) return false;
  if (a.serviceName !== b.serviceName) return false;
  if (a.weightUnit !== b.weightUnit) return false;
  if (a.dimensionUnit !== b.dimensionUnit) return false;
  const av = a.stageVocabulary;
  const bv = b.stageVocabulary;
  if (av.length !== bv.length) return false;
  return av.every((value, index) => value === bv[index]);
}

function validate(draft) {
  const errors = {};
  const name = draft.serviceName.trim();

  if (!name) {
    errors.serviceName = 'Service name is required. Previous confirmed value retained.';
  } else if (name.length > SERVICE_NAME_MAX) {
    errors.serviceName =
      'Service name is limited to ' + SERVICE_NAME_MAX + ' characters.';
  }

  const stages = draft.stageVocabulary;
  const stageErrors = stages.map(() => '');

  if (stages.length < STAGE_COUNT_MIN) {
    errors.stageVocabulary =
      'At least ' + STAGE_COUNT_MIN + ' custody stages are required.';
  } else if (stages.length > STAGE_COUNT_MAX) {
    errors.stageVocabulary =
      'No more than ' + STAGE_COUNT_MAX + ' custody stages are permitted.';
  }

  stages.forEach((stage, index) => {
    const value = stage.trim();
    if (!value) {
      stageErrors[index] = 'Stage ' + (index + 1) + ' requires a label.';
      return;
    }
    if (value.length > STAGE_LABEL_MAX) {
      stageErrors[index] =
        'Stage ' + (index + 1) + ' is limited to ' + STAGE_LABEL_MAX + ' characters.';
      return;
    }
    const firstIndex = stages.findIndex(
      (other) => other.trim().toLowerCase() === value.toLowerCase()
    );
    if (firstIndex !== index) {
      stageErrors[index] = 'Duplicate stage label — each stage must be unique.';
    }
  });

  if (!errors.stageVocabulary && stageErrors.some(Boolean)) {
    errors.stageVocabulary = 'Correct the highlighted stage entries and re-save.';
  }
  if (stageErrors.some(Boolean)) {
    errors.stageErrors = stageErrors;
  }

  if (!WEIGHT_UNITS.includes(draft.weightUnit)) {
    errors.weightUnit = 'Choose a supported default weight unit.';
  }
  if (!DIMENSION_UNITS.includes(draft.dimensionUnit)) {
    errors.dimensionUnit = 'Choose a supported default dimension unit.';
  }

  return errors;
}

function normalizeDraft(draft) {
  return {
    serviceName: draft.serviceName.trim(),
    stageVocabulary: draft.stageVocabulary.map((stage) => stage.trim()),
    weightUnit: draft.weightUnit,
    dimensionUnit: draft.dimensionUnit,
  };
}

function firstInvalidField(errors) {
  const order = ['serviceName', 'stageVocabulary', 'weightUnit', 'dimensionUnit'];
  return order.find((field) => Boolean(errors[field])) || '';
}

/* ---------------------------------------------------------------- icons */

function ChevronIcon({ direction }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={direction === 'up' ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ---------------------------------------------------------------- row */

function ConfigRow({ label, hint, controlId, error, children }) {
  return (
    <div className="acc-row">
      <div className="acc-label-col">
        <label className="acc-label" htmlFor={controlId}>
          {label}
        </label>
        {hint ? <span className="acc-label-hint">{hint}</span> : null}
      </div>
      <div className="acc-control-col">
        {children}
        {error ? (
          <p className="acc-msg acc-msg-error" role="alert">
            <span>{error}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- section */

function AdminConfigurationConsole() {
  const { data, loading, error, refresh } = useProjectCollection(CONFIG_COLLECTION);

  const record = data && data.length > 0 ? data[0] : null;

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [confirmed, setConfirmed] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle');
  const [announcement, setAnnouncement] = useState('');
  const [seededFrom, setSeededFrom] = useState(null);

  const savingRef = useRef(false);

  const isNewRecord = !loading && !error && !record;
  const listRevision =
    data && data.length > 0
      ? String(data[0].id) + '@' + String(data[0].updatedAt || '')
      : '';

  /* Seed the draft from the confirmed record on first load only. */
  useEffect(() => {
    if (loading || error) return;
    if (seededFrom === listRevision) return;
    const next = configToDraft(record);
    setDraft(next);
    setConfirmed(next);
    setFieldErrors({});
    setSaveStatus('idle');
    setSeededFrom(listRevision);
  }, [loading, error, seededFrom, listRevision, record]);

  const stageErrorList = Array.isArray(fieldErrors.stageErrors)
    ? fieldErrors.stageErrors
    : null;

  const isDirty = useMemo(
    () => Boolean(confirmed) && !draftsMatch(draft, confirmed),
    [draft, confirmed]
  );

  const busy = saveStatus === 'saving';

  /* -------------------------------------------------- field handlers */

  function patchField(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (saveStatus !== 'idle') setSaveStatus('idle');
    setAnnouncement('');
  }

  function updateStage(index, value) {
    setDraft((prev) => {
      const stages = prev.stageVocabulary.slice();
      stages[index] = value;
      return { ...prev, stageVocabulary: stages };
    });
    setFieldErrors((prev) => {
      if (!prev.stageErrors && !prev.stageVocabulary) return prev;
      const next = { ...prev };
      delete next.stageVocabulary;
      if (next.stageErrors) {
        const list = next.stageErrors.slice();
        list[index] = '';
        next.stageErrors = list;
      }
      return next;
    });
    if (saveStatus !== 'idle') setSaveStatus('idle');
    setAnnouncement('');
  }

  function moveStage(index, delta) {
    setDraft((prev) => {
      const target = index + delta;
      const stages = prev.stageVocabulary.slice();
      if (target < 0 || target >= stages.length) return prev;
      const swap = stages[index];
      stages[index] = stages[target];
      stages[target] = swap;
      return { ...prev, stageVocabulary: stages };
    });
    setFieldErrors({});
    if (saveStatus !== 'idle') setSaveStatus('idle');
    setAnnouncement('');
  }

  /* -------------------------------------------------- save lifecycle */

  async function handleSubmit(event) {
    event.preventDefault();
    if (savingRef.current) return;

    const errors = validate(draft);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSaveStatus('error');
      setAnnouncement('Configuration rejected — correct the highlighted values.');
      const focusId = firstInvalidField(errors);
      if (focusId) {
        const node = document.getElementById('acc-' + focusId);
        if (node && typeof node.focus === 'function') node.focus();
      }
      return;
    }

    setFieldErrors({});
    setSaveStatus('saving');
    setAnnouncement('Saving configuration.');
    savingRef.current = true;

    const normalized = normalizeDraft(draft);
    const payload = {
      serviceName: normalized.serviceName,
      stageVocabulary: normalized.stageVocabulary,
      weightUnit: normalized.weightUnit,
      dimensionUnit: normalized.dimensionUnit,
    };

    try {
      if (record && record.id != null) {
        await projectData.update(CONFIG_COLLECTION, record.id, payload);
      } else {
        await projectData.create(CONFIG_COLLECTION, {
          id: 'svc-config-ffds',
          updatedAt: new Date().toISOString().slice(0, 10),
          ...payload,
        });
      }
      savingRef.current = false;
      setDraft(normalized);
      setConfirmed(normalized);
      setSaveStatus('saved');
      setAnnouncement('Configuration saved.');
      refresh();
    } catch (writeError) {
      savingRef.current = false;
      setSaveStatus('error');
      setFieldErrors({});
      setAnnouncement(
        'Configuration could not be saved. Your edits are retained — re-save to retry.'
      );
      setConfirmed(record ? configToDraft(record) : null);
    }
  }

  /* -------------------------------------------------- derived display */

  const stageList = draft.stageVocabulary.length > 0
    ? draft.stageVocabulary
    : DEFAULT_STAGES;

  const confirmedStages =
    confirmed && confirmed.stageVocabulary.length > 0
      ? confirmed.stageVocabulary
      : DEFAULT_STAGES;

  const chainSummary =
    confirmedStages.length > 0
      ? confirmedStages.join(' → ')
      : 'Not configured';

  const updatedAtLabel = record && record.updatedAt ? String(record.updatedAt) : '—';

  const formReadOnly = updateCapabilityMissing();

  function updateCapabilityMissing() {
    return (
      typeof projectData.create !== 'function' ||
      typeof projectData.update !== 'function'
    );
  }

  /* -------------------------------------------------- render */

  return (
    <section
      className="acc-root"
      aria-label="Service configuration console"
      id="adminconfigurationconsole-section"
    >
      <div className="acc-shell">
        <div className="acc-panel">
          <div className="acc-head">
            <div>
              <span className="acc-head-label">Instrument calibration</span>
              <h2 className="acc-head-title">Service Configuration</h2>
            </div>
            <div className="acc-head-meta">
              <span>Configuration record</span>
              <strong>{record ? String(record.id) : 'svc-config-ffds'}</strong>
              <span>Updated</span>
              <strong>{updatedAtLabel}</strong>
            </div>
          </div>

          {error ? (
            <div className="acc-rows">
              <p className="acc-msg acc-msg-error" role="alert">
                <span>Configuration could not be loaded: {String(error.message || error)}</span>
              </p>
              <div className="acc-savebar">
                <div className="acc-save-status">
                  <p className="acc-note">
                    The console is unavailable until the configuration record loads.
                  </p>
                </div>
                <button
                  type="button"
                  className="acc-save-btn"
                  onClick={() => refresh()}
                >
                  Retry load
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="acc-rows" aria-hidden="true">
              {['Service name', 'Stage vocabulary', 'Weight unit', 'Dimension unit', 'Custody stages'].map(
                (key) => (
                  <div className="acc-skeleton-row" key={key}>
                    <span className="acc-sk acc-sk-label" />
                    <span className="acc-sk acc-sk-control" />
                  </div>
                )
              )}
            </div>
          ) : (
            <form className="acc-form" onSubmit={handleSubmit} noValidate>
              <div className="acc-rows">
                {/* ---- service name ---- */}
                <ConfigRow
                  label="Service name"
                  hint="Displayed as the operator identity across every surface."
                  controlId="acc-serviceName"
                  error={fieldErrors.serviceName}
                >
                  <input
                    id="acc-serviceName"
                    className="acc-input"
                    type="text"
                    value={draft.serviceName}
                    maxLength={SERVICE_NAME_MAX + 20}
                    autoComplete="organization"
                    aria-invalid={fieldErrors.serviceName ? 'true' : undefined}
                    onChange={(event) => patchField('serviceName', event.target.value)}
                  />
                </ConfigRow>

                {/* ---- stage vocabulary ---- */}
                <div className="acc-row">
                  <div className="acc-label-col">
                    <span className="acc-label" id="acc-stageVocabulary-label">
                      Stage vocabulary
                    </span>
                    <span className="acc-label-hint">
                      Ordered custody chain — the gauge sweeps through these stages
                      in order.
                    </span>
                  </div>
                  <div className="acc-control-col">
                    <div
                      className="acc-stages"
                      role="group"
                      aria-labelledby="acc-stageVocabulary-label"
                    >
                      {stageList.map((stage, index) => {
                        const stageError = stageErrorList ? stageErrorList[index] : '';
                        return (
                          <div
                            className="acc-stage"
                            key={'stage-' + index}
                            data-invalid={stageError ? 'true' : undefined}
                          >
                            <span className="acc-stage-index" aria-hidden="true">
                              {index + 1}
                            </span>
                            <input
                              id={index === 0 ? 'acc-stageVocabulary' : undefined}
                              className="acc-stage-input"
                              type="text"
                              value={stage}
                              maxLength={STAGE_LABEL_MAX + 12}
                              aria-label={
                                'Stage ' + (index + 1) + ' of ' + stageList.length
                              }
                              aria-invalid={stageError ? 'true' : undefined}
                              onChange={(event) => updateStage(index, event.target.value)}
                            />
                            <span className="acc-stage-moves">
                              <button
                                type="button"
                                className="acc-move-btn"
                                disabled={index === 0}
                                aria-label={
                                  'Move stage ' + (index + 1) + ' earlier in the chain'
                                }
                                onClick={() => moveStage(index, -1)}
                              >
                                <ChevronIcon direction="up" />
                              </button>
                              <button
                                type="button"
                                className="acc-move-btn"
                                disabled={index === stageList.length - 1}
                                aria-label={
                                  'Move stage ' + (index + 1) + ' later in the chain'
                                }
                                onClick={() => moveStage(index, 1)}
                              >
                                <ChevronIcon direction="down" />
                              </button>
                            </span>
                            {stageError ? (
                              <p
                                className="acc-msg acc-msg-error"
                                role="alert"
                                style={{ gridColumn: '1 / -1' }}
                              >
                                <span>{stageError}</span>
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    {fieldErrors.stageVocabulary ? (
                      <p className="acc-msg acc-msg-error" role="alert">
                        <span>{fieldErrors.stageVocabulary}</span>
                      </p>
                    ) : null}
                  </div>
                </div>

                {/* ---- default units ---- */}
                <div className="acc-row">
                  <div className="acc-label-col">
                    <span className="acc-label">Default units</span>
                    <span className="acc-label-hint">
                      Applied to every consignment record and ruled data row.
                    </span>
                  </div>
                  <div className="acc-control-col">
                    <div className="acc-grid-2">
                      <div className="acc-control-col">
                        <label className="acc-label" htmlFor="acc-weightUnit">
                          Weight unit
                        </label>
                        <select
                          id="acc-weightUnit"
                          className="acc-select"
                          value={draft.weightUnit}
                          aria-invalid={fieldErrors.weightUnit ? 'true' : undefined}
                          onChange={(event) => patchField('weightUnit', event.target.value)}
                        >
                          {WEIGHT_UNITS.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                        {fieldErrors.weightUnit ? (
                          <p className="acc-msg acc-msg-error" role="alert">
                            <span>{fieldErrors.weightUnit}</span>
                          </p>
                        ) : null}
                      </div>
                      <div className="acc-control-col">
                        <label className="acc-label" htmlFor="acc-dimensionUnit">
                          Dimension unit
                        </label>
                        <select
                          id="acc-dimensionUnit"
                          className="acc-select"
                          value={draft.dimensionUnit}
                          aria-invalid={fieldErrors.dimensionUnit ? 'true' : undefined}
                          onChange={(event) =>
                            patchField('dimensionUnit', event.target.value)
                          }
                        >
                          {DIMENSION_UNITS.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                        {fieldErrors.dimensionUnit ? (
                          <p className="acc-msg acc-msg-error" role="alert">
                            <span>{fieldErrors.dimensionUnit}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- save bar ---- */}
              <div className="acc-savebar">
                <div className="acc-save-status">
                  <p className="acc-note">
                    Current chain: <strong>{chainSummary}</strong>
                  </p>
                  {isNewRecord ? (
                    <p className="acc-msg acc-msg-note">
                      <span>
                        No configuration record exists yet — saving creates
                        svc-config-ffds.
                      </span>
                    </p>
                  ) : null}
                  {saveStatus === 'saved' ? (
                    <p className="acc-msg acc-msg-ok" role="status">
                      <span>
                        Configuration saved — in effect on the shipment workspace
                        and tracking surface.
                      </span>
                    </p>
                  ) : null}
                  {saveStatus === 'error' && announcement ? (
                    <p className="acc-msg acc-msg-error" role="alert">
                      <span>{announcement}</span>
                    </p>
                  ) : null}
                  {saveStatus === 'idle' && isDirty ? (
                    <p className="acc-msg acc-msg-note">
                      <span>Unsaved changes — save to apply them.</span>
                    </p>
                  ) : null}
                </div>

                <button
                  type="submit"
                  className={'acc-save-btn' + (isDirty ? ' is-dirty' : '')}
                  disabled={busy || formReadOnly}
                  aria-busy={busy ? 'true' : 'false'}
                >
                  {busy ? <span className="acc-spinner" aria-hidden="true" /> : null}
                  {busy ? 'Saving' : 'Save configuration'}
                  <span className="acc-dirty-dot" aria-hidden="true" />
                </button>
              </div>

              <p className="acc-visually-hidden" role="status" aria-live="polite">
                {announcement}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default AdminConfigurationConsole;